import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/lib/auth";
import { CurrencyProvider } from "@/lib/currency";
import PWAInstall from "@/components/PWAInstall";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Discover from "@/pages/Discover";
import Explore from "@/pages/Explore";
import EventLondon from "@/pages/EventLondon";
import CreatorPublicProfile from "@/pages/CreatorPublicProfile";
import BrandPublicProfile from "@/pages/BrandPublicProfile";
import CreatorDashboard from "@/pages/CreatorDashboard";
import BrandDashboard from "@/pages/BrandDashboard";
import { BrandDashboard as BrandDashboardV2, BrandApplications, BrandOrders, BrandAnalytics, BrandSaved, BrandNotifications, BrandSettings, BrandApprovals, BrandPayments, BrandBilling } from "@/pages/BrandPortal";
import PaymentResult from "@/pages/PaymentResult";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import DataDeletion from "@/pages/DataDeletion";
import AdminPayouts from "@/pages/AdminPayouts";
import AdminDisputes from "@/pages/AdminDisputes";
import AdminRaffle from "@/pages/AdminRaffle";
import AdminSignups from "@/pages/AdminSignups";
import AdminStudio from "@/pages/AdminStudio";
import AdminPromo from "@/pages/AdminPromo";
import AdminIdentity from "@/pages/AdminIdentity";
import AdminShowcase from "@/pages/AdminShowcase";
import Results from "@/pages/Results";
import Notifications from "@/pages/Notifications";
import AdminBroadcast from "@/pages/AdminBroadcast";
import AdminLeads from "@/pages/AdminLeads";
import AccountSettings from "@/pages/AccountSettings";
import WinCard from "@/pages/WinCard";
import DealRoom from "@/pages/DealRoom";
import Pricing from "@/pages/Pricing";
import HowItWorks from "@/pages/HowItWorks";
import SetPassword from "@/pages/SetPassword";
import Affiliates from "@/pages/Affiliates";
import BrandAffiliates from "@/pages/BrandAffiliates";
import Groups from "@/pages/Groups";
import AdminAdQueries from "@/pages/AdminAdQueries";
import PartnerIIA from "@/pages/PartnerIIA";
import AdminPartners from "@/pages/AdminPartners";
import ProfileEditor from "@/pages/ProfileEditor";
import PayoutMethod from "@/pages/PayoutMethod";
import CampaignsList from "@/pages/CampaignsList";
import CampaignNew from "@/pages/CampaignNew";
import CampaignDetail from "@/pages/CampaignDetail";
import Leaderboard from "@/pages/Leaderboard";
import HallOfFame from "@/pages/HallOfFame";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";
import CaseStudiesList from "@/pages/CaseStudiesList";
import CaseStudyDetail from "@/pages/CaseStudyDetail";
import Wallet from "@/pages/Wallet";
import CreatorEarnings from "@/pages/CreatorEarnings";
import CreatorStudio from "@/pages/CreatorStudio";
import GrowthAI from "@/pages/growth/GrowthAI";
import Negotiations from "@/pages/Negotiations";
import Collabs from "@/pages/Collabs";
import Messages from "@/pages/Messages";
import MessageThread from "@/pages/MessageThread";
import Academy from "@/pages/Academy";
import AcademyModule from "@/pages/AcademyModule";
import Inspiration from "@/pages/Inspiration";
import Contact from "@/pages/Contact";
import Trust from "@/pages/Trust";
import "@/App.css";

function OAuthGuard({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return children;
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
        <CurrencyProvider>
          <OAuthGuard>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/discover" element={<Discover />} />
            <Route path="/explore" element={<Explore />} />
            <Route path="/london" element={<EventLondon />} />
            <Route path="/creators/:id" element={<CreatorPublicProfile />} />
            <Route path="/brands/:id" element={<BrandPublicProfile />} />
            <Route path="/creator" element={<CreatorDashboard />} />
            <Route path="/creator/profile" element={<ProfileEditor />} />
            <Route path="/creator/payout-method" element={<PayoutMethod />} />
            <Route path="/brand" element={<BrandDashboardV2 />} />
            <Route path="/brand/legacy" element={<BrandDashboard />} />
            <Route path="/brand/applications" element={<BrandApplications />} />
            <Route path="/brand/approvals" element={<BrandApprovals />} />
            <Route path="/brand/orders" element={<BrandOrders />} />
            <Route path="/brand/payments" element={<BrandPayments />} />
            <Route path="/brand/billing" element={<BrandBilling />} />
            <Route path="/payment/success" element={<PaymentResult />} />
            <Route path="/payment/cancel" element={<PaymentResult variant="cancel" />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route path="/admin/payouts" element={<AdminPayouts />} />
            <Route path="/admin/disputes" element={<AdminDisputes />} />
            <Route path="/admin/raffle" element={<AdminRaffle />} />
            <Route path="/admin/signups" element={<AdminSignups />} />
            <Route path="/admin/studio" element={<AdminStudio />} />
            <Route path="/admin/promo" element={<AdminPromo />} />
            <Route path="/admin/identity" element={<AdminIdentity />} />
            <Route path="/admin/showcase" element={<AdminShowcase />} />
            <Route path="/results" element={<Results />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/admin/broadcast" element={<AdminBroadcast />} />
            <Route path="/admin/leads" element={<AdminLeads />} />
            <Route path="/settings" element={<AccountSettings />} />
            <Route path="/win/:drawId" element={<WinCard />} />
            <Route path="/brand/analytics" element={<BrandAnalytics />} />
            <Route path="/brand/saved" element={<BrandSaved />} />
            <Route path="/brand/notifications" element={<BrandNotifications />} />
            <Route path="/brand/settings" element={<BrandSettings />} />
            <Route path="/deals/:id" element={<DealRoom />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/affiliates" element={<Affiliates />} />
            <Route path="/brand/affiliates" element={<BrandAffiliates />} />
            <Route path="/groups" element={<Groups />} />
            <Route path="/admin/ad-queries" element={<AdminAdQueries />} />
            <Route path="/partners/iia" element={<PartnerIIA />} />
            <Route path="/admin/partners" element={<AdminPartners />} />
            <Route path="/campaigns" element={<CampaignsList />} />
            <Route path="/campaigns/new" element={<CampaignNew />} />
            <Route path="/campaigns/:id" element={<CampaignDetail />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/hall-of-fame" element={<HallOfFame />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/case-studies" element={<CaseStudiesList />} />
            <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
            <Route path="/wallet" element={<Wallet />} />
            <Route path="/creator/earnings" element={<CreatorEarnings />} />
            <Route path="/creator-studio" element={<CreatorStudio />} />
            <Route path="/growth" element={<GrowthAI />} />
            <Route path="/negotiations" element={<Negotiations />} />
            <Route path="/collabs" element={<Collabs />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/messages/:hireId" element={<MessageThread />} />
            <Route path="/academy" element={<Academy />} />
            <Route path="/academy/:moduleId" element={<AcademyModule />} />
            <Route path="/inspiration" element={<Inspiration />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/trust" element={<Trust />} />
          </Routes>
          </OAuthGuard>
          <PWAInstall />
          <Toaster
            theme="dark"
            position="top-right"
            offset="calc(env(safe-area-inset-top, 0px) + 16px)"
            mobileOffset="calc(env(safe-area-inset-top, 0px) + 16px)"
            toastOptions={{
              style: {
                background: "#0C0C10",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.1)",
              },
            }}
          />
        </CurrencyProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
